import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  PlanEntitlementKey,
  SubscribeToFeaturesDocument,
  SubscribeToFeaturesSubscriptionResult,
  useGetFeaturesQuery,
} from '../graphql/generated';
import { useApolloSharedSubscription } from '../subscription/apollo-subscription.manager';

type Features = Record<string, unknown>;

export const useGetFeatures = (): Features => {
  const { data: getFeaturesData, loading } = useGetFeaturesQuery({
    fetchPolicy: 'cache-only',
  });

  const { data: subscriptionFeatureData } =
    useApolloSharedSubscription<SubscribeToFeaturesSubscriptionResult>({
      query: SubscribeToFeaturesDocument,
    });

  const [features, setFeatures] = useState<Features>({});

  useEffect(() => {
    if (getFeaturesData && !loading) {
      setFeatures(getFeaturesData.getFeatures);
    }
  }, [getFeaturesData, loading]);

  useEffect(() => {
    if (subscriptionFeatureData?.subscribeToFeatures) {
      setFeatures(subscriptionFeatureData.subscribeToFeatures);
    }
  }, [subscriptionFeatureData?.subscribeToFeatures]);

  return features;
};

export const useHasFeature = <T>(
  featureName: string | undefined
): T | boolean => {
  const features = useGetFeatures();

  if (isNil(featureName)) {
    return true;
  }

  if (features) {
    return (features[featureName] as T) ?? false;
  }

  return false;
};

export type HasFeatureCallback = <T>(featureName: string) => T | boolean;

export const useHasFeatureLazy = (): HasFeatureCallback => {
  const features = useGetFeatures();

  const hasFeature = useCallback<HasFeatureCallback>(
    <T>(featureName: string): T | boolean => {
      if (features) {
        return (features[featureName] as T) ?? false;
      }

      return false;
    },
    [features]
  );

  return hasFeature;
};

/**
 * Semantically, a "beta user" is a user who has been granted access to a
 * paywalled feature without requiring an entitlement.
 *
 * By convention, checks for a feature flag named `${entitlementKey}-beta-user`.
 *
 * Returns false if no entitlement key is provided, or if the "beta-user" flag
 * is not assigned to the user. Otherwise, returns true.
 */
export const useIsFeatureBetaUser = (
  entitlementKey?: PlanEntitlementKey
): boolean => {
  const isBetaUser = useIsFeatureBetaUserLazy();
  return isBetaUser(entitlementKey);
};

export type IsFeatureBetaUserCallback = (
  entitlementKey?: PlanEntitlementKey
) => boolean;

export const useIsFeatureBetaUserLazy = (): IsFeatureBetaUserCallback => {
  const hasFeature = useHasFeatureLazy();

  const isBetaUser = useCallback<IsFeatureBetaUserCallback>(
    (entitlementKey?: PlanEntitlementKey): boolean => {
      if (!entitlementKey) {
        return false;
      }
      return hasFeature<boolean>(`${entitlementKey}-beta-user`);
    },
    [hasFeature]
  );

  return isBetaUser;
};
